import React from "react";
import { ActionIcon, Button, Divider, Grid, Group, Paper, Select, Stack, Text, TextInput } from "@mantine/core";
import { useDebouncedCallback } from "@mantine/hooks";
import { Icon } from "@iconify/react";
import { GuidelineMetaData, GuidelineMetaDataQuestion } from "src/state/_types";
import { GenericFieldProps } from "src/utils/forms";

const QUESTION_TYPES: { value: GuidelineMetaDataQuestion['type']; label: string }[] = [
    { value: 'text', label: 'Text' },
    { value: 'textarea', label: 'Text Area' },
    { value: 'number', label: 'Number' },
    { value: 'boolean', label: 'Yes / No' },
    { value: 'date', label: 'Date' },
    { value: 'datetime', label: 'Date & Time' },
    { value: 'time', label: 'Time' },
    { value: 'enum', label: 'Select' },
    { value: 'multi-select', label: 'Multi Select' },
    { value: 'currency', label: 'Currency' },
    { value: 'email', label: 'Email' },
    { value: 'phone', label: 'Phone' },
    { value: 'url', label: 'URL' },
    { value: 'rating', label: 'Rating' },
];

const typesWithOptions: GuidelineMetaDataQuestion['type'][] = ['enum', 'multi-select'];

function parseQuestions(value: string | null | undefined): GuidelineMetaDataQuestion[] {
    if (!value) return [];
    try {
        const parsed: GuidelineMetaData = JSON.parse(value);
        return parsed.questions || [];
    } catch {
        return [];
    }
}

function serializeQuestions(questions: GuidelineMetaDataQuestion[]): string {
    const meta: GuidelineMetaData = { questions };
    return JSON.stringify(meta);
}

export const GuidelineMetaDataBuilder: React.FC<GenericFieldProps> = ({ label, onChange, value, classNames }) => {

    const [questions, setQuestions] = React.useState<GuidelineMetaDataQuestion[]>(() => parseQuestions(value));

    const debouncedOnChange = useDebouncedCallback((updated: GuidelineMetaDataQuestion[]) => {
        onChange(serializeQuestions(updated));
    }, 300);

    const sync = (updated: GuidelineMetaDataQuestion[]) => {
        setQuestions(updated);
        debouncedOnChange(updated);
    };

    const addQuestion = () => {
        sync([...questions, { question: '', answer: null, type: 'text', required: false }]);
    };

    const removeQuestion = (index: number) => {
        sync(questions.filter((_, i) => i !== index));
    };

    const moveQuestion = (index: number, direction: -1 | 1) => {
        const target = index + direction;
        if (target < 0 || target >= questions.length) return;
        const updated = [...questions];
        [updated[index], updated[target]] = [updated[target], updated[index]];
        sync(updated);
    };

    const updateQuestion = (index: number, patch: Partial<GuidelineMetaDataQuestion>) => {
        const updated = questions.map((q, i) => i === index ? { ...q, ...patch } : q);
        sync(updated);
    };

    return (
        <div className={classNames.root}>
            <Stack gap="sm" className={classNames.wrapper}>
                <Text fw={500} size="sm" className={classNames.label}>{label}</Text>

                {questions.map((q, index) => (
                    <Paper key={index} withBorder p="xs">


                        <Group justify="space-between" align="center">

                            <Group gap="2" align="center">
                                <Text fw={500} size="sm" className={classNames.label}>Question {index+1}</Text>
                            </Group>

                            <Group gap="2" wrap="nowrap" justify="flex-end" align="center">
                                <ActionIcon
                                    variant="subtle"
                                    size="sm"
                                    disabled={index === 0}
                                    onClick={() => moveQuestion(index, -1)}
                                >
                                    <Icon icon="mdi:arrow-up" width={16} />
                                </ActionIcon>
                                <ActionIcon
                                    variant="subtle"
                                    size="sm"
                                    disabled={index === questions.length - 1}
                                    onClick={() => moveQuestion(index, 1)}
                                >
                                    <Icon icon="mdi:arrow-down" width={16} />
                                </ActionIcon>
                                <ActionIcon
                                    variant="subtle"
                                    color="red"
                                    size="sm"
                                    onClick={() => removeQuestion(index)}
                                >
                                    <Icon icon="mdi:close" width={16} />
                                </ActionIcon>
                            </Group>

                        </Group>

                        <Divider variant="dashed" my={6}/>

                        <Grid>
                            <Grid.Col span={6}>
                                <Text fw={500} size="xs" className={classNames.label}>Question</Text>
                                <TextInput
                                    size="xs"
                                    value={q.question}
                                    onChange={(e) => updateQuestion(index, { question: e.currentTarget.value })}
                                />
                            </Grid.Col>
                            <Grid.Col span={4}>
                                <Text fw={500} size="xs" className={classNames.label}>Type</Text>
                                <Select
                                    size="xs"
                                    data={QUESTION_TYPES}
                                    value={q.type}
                                    onChange={(val) => {
                                        const type = val as GuidelineMetaDataQuestion['type'];
                                        const patch: Partial<GuidelineMetaDataQuestion> = { type };
                                        if (!typesWithOptions.includes(type)) {
                                            patch.options = undefined;
                                        }
                                        updateQuestion(index, patch);
                                    }}
                                    allowDeselect={false}
                                />
                            </Grid.Col>
                            <Grid.Col span={2}>
                                <Text fw={500} size="xs" className={classNames.label}>Required</Text>
                                <Select
                                    size="xs"
                                    value={q.required ? 'true' : 'false'}
                                    onChange={(value) => updateQuestion(index, { required: value === 'true' })}
                                    data={[
                                        { label: 'Yes', value: 'true' },
                                        { label: 'No', value: 'false' },
                                    ]}
                                />
                            </Grid.Col>
                            {typesWithOptions.includes(q.type) && (
                                <Grid.Col span={12}>
                                    <TextInput
                                        label="Options (comma-separated)"
                                        size="xs"
                                        value={(q.options || []).map(o => typeof o === 'string' ? o : o.label).join(', ')}
                                        onChange={(e) => {
                                            const raw = e.currentTarget.value;
                                            const options = raw.split(',').map(s => s.trim());
                                            updateQuestion(index, { options });
                                        }}
                                    />
                                </Grid.Col>
                            )}
                        </Grid>
                    </Paper>
                ))}

                <Button
                    variant="light"
                    size="xs"
                    leftSection={<Icon icon="mdi:plus" width={16} />}
                    onClick={addQuestion}
                >
                    Add Question
                </Button>
            </Stack>
        </div>
    );
};
